import { UniversalCamera } from "@babylonjs/core/Cameras/universalCamera";
import { Engine } from "@babylonjs/core/Engines/engine";
import { DirectionalLight } from "@babylonjs/core/Lights/directionalLight";
import { HemisphericLight } from "@babylonjs/core/Lights/hemisphericLight";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Scene } from "@babylonjs/core/scene";

export const PROGRAMMATIC_SCENE_IDS = [
  "tile-exhibition-hall",
  "gravel-park-path",
  "fallen-leaf-forest-path",
] as const;

export type ProgrammaticSceneId = (typeof PROGRAMMATIC_SCENE_IDS)[number];

interface CameraPreset {
  readonly label: string;
  readonly position: Vector3;
  readonly target: Vector3;
}

interface SceneDefinition {
  readonly number: string;
  readonly title: string;
  readonly category: "室内" | "室外";
  readonly ariaLabel: string;
  readonly cameras: readonly CameraPreset[];
}

const DEFINITIONS: Readonly<Record<ProgrammaticSceneId, SceneDefinition>> = {
  "tile-exhibition-hall": {
    number: "02",
    title: "瓷砖展厅",
    category: "室内",
    ariaLabel: "瓷砖展厅三维原型",
    cameras: [
      { label: "入口全景", position: new Vector3(0, 1.65, -3.75), target: new Vector3(0, 1.05, 1.35) },
      { label: "左展台斜角", position: new Vector3(-4.55, 1.65, -2.15), target: new Vector3(0.65, 1, 1.25) },
      { label: "右展台斜角", position: new Vector3(4.55, 1.65, -1.8), target: new Vector3(-0.7, 1.05, 1.45) },
      { label: "后墙回望", position: new Vector3(-3.9, 1.55, 2.75), target: new Vector3(0.45, 1.05, -1.9) },
      { label: "瓷砖低视角", position: new Vector3(3.8, 1.28, -2.95), target: new Vector3(0.1, 0.28, 0.8) },
    ],
  },
  "gravel-park-path": {
    number: "03",
    title: "碎石公园路",
    category: "室外",
    ariaLabel: "碎石公园路三维原型",
    cameras: [
      { label: "路口全景", position: new Vector3(0, 1.65, -7.7), target: new Vector3(0, 1.05, 3.4) },
      { label: "左侧树下", position: new Vector3(-4.9, 1.7, -4.3), target: new Vector3(0.2, 1.05, 2.2) },
      { label: "右侧长椅", position: new Vector3(4.7, 1.62, -1.6), target: new Vector3(-0.2, 1.05, 3.1) },
      { label: "中段回望", position: new Vector3(-2.7, 1.58, 5.3), target: new Vector3(0.1, 1, -4.2) },
      { label: "碎石低视角", position: new Vector3(2.65, 1.25, -5.8), target: new Vector3(0, 0.2, 1.7) },
    ],
  },
  "fallen-leaf-forest-path": {
    number: "04",
    title: "落叶森林路",
    category: "室外",
    ariaLabel: "落叶森林路三维原型",
    cameras: [
      { label: "林道入口", position: new Vector3(0, 1.65, -7.8), target: new Vector3(0, 1.1, 3.5) },
      { label: "左侧林缘", position: new Vector3(-4.7, 1.68, -3.6), target: new Vector3(0.15, 1.05, 2.8) },
      { label: "右侧树间", position: new Vector3(4.55, 1.6, -1.25), target: new Vector3(-0.25, 1, 3.35) },
      { label: "深林回望", position: new Vector3(-2.9, 1.56, 5.6), target: new Vector3(0.2, 1, -4.4) },
      { label: "落叶低视角", position: new Vector3(2.5, 1.2, -5.5), target: new Vector3(0, 0.18, 1.5) },
    ],
  },
};

export function isProgrammaticSceneId(value: string | null): value is ProgrammaticSceneId {
  return PROGRAMMATIC_SCENE_IDS.includes(value as ProgrammaticSceneId);
}

export class StimulusScenePrototype {
  private readonly canvas: HTMLCanvasElement;
  private readonly engine: Engine;
  private readonly scene: Scene;
  private readonly camera: UniversalCamera;
  private readonly definition: SceneDefinition;
  private activeView = 0;
  private randomState = 0x9e3779b9;

  constructor(root: HTMLElement, private readonly sceneId: ProgrammaticSceneId) {
    this.definition = DEFINITIONS[sceneId];
    root.innerHTML = `
      <div class="prototype-shell">
        <canvas id="prototype-canvas" aria-label="${this.definition.ariaLabel}"></canvas>
        <header class="prototype-header">
          <div><p>Stimulus Prototype ${this.definition.number} · ${this.definition.category}</p><h1>${this.definition.title}</h1><span>同一3D场景 · 五个固定镜头 · Quest 3预算原型</span></div>
          <a href="./">返回训练</a>
        </header>
        <nav class="prototype-camera-bar" aria-label="镜头角度">
          ${this.definition.cameras.map((preset, index) => `<button type="button" data-view="${index}">${index + 1}. ${preset.label}</button>`).join("")}
          <button id="download-prototype" type="button">下载当前PNG</button>
        </nav>
        <aside class="prototype-note">程序化低多边形刺激图；四场景统一16:9、统一视高与中性照明。</aside>
      </div>`;
    const canvas = root.querySelector("#prototype-canvas");
    if (!(canvas instanceof HTMLCanvasElement)) throw new Error("Missing prototype canvas");
    this.canvas = canvas;
    root.classList.toggle("prototype-render-only", new URLSearchParams(location.search).get("render") === "1");
    this.engine = new Engine(canvas, true, { preserveDrawingBuffer: true, stencil: true });
    this.scene = new Scene(this.engine);
    this.camera = new UniversalCamera("prototype-camera", this.definition.cameras[0]?.position.clone() ?? Vector3.Zero(), this.scene);
    this.camera.fov = 0.93;
    this.camera.minZ = 0.05;
    this.scene.activeCamera = this.camera;
    this.buildScene();
    root.querySelectorAll<HTMLButtonElement>("[data-view]").forEach((button) => {
      button.addEventListener("click", () => this.setView(Number(button.dataset.view ?? 0)));
    });
    root.querySelector<HTMLButtonElement>("#download-prototype")?.addEventListener("click", () => this.downloadCurrentView());
    window.addEventListener("resize", () => this.engine.resize());
    const requested = Math.max(0, Math.min(4, Number(new URLSearchParams(location.search).get("view") ?? 1) - 1));
    this.setView(requested);
    this.engine.runRenderLoop(() => this.scene.render());
  }

  private setView(index: number): void {
    const preset = this.definition.cameras[index];
    if (!preset) return;
    this.activeView = index;
    this.camera.position.copyFrom(preset.position);
    this.camera.setTarget(preset.target);
    document.querySelectorAll<HTMLButtonElement>("[data-view]").forEach((button) => {
      button.classList.toggle("active", Number(button.dataset.view) === index);
    });
  }

  private buildScene(): void {
    if (this.sceneId === "tile-exhibition-hall") this.buildExhibitionHall();
    else if (this.sceneId === "gravel-park-path") this.buildGravelPark();
    else this.buildLeafForest();
  }

  private buildExhibitionHall(): void {
    this.scene.clearColor = Color4.FromHexString("#cbd2d5ff");
    this.scene.ambientColor = Color3.FromHexString("#6c7275");
    new HemisphericLight("hall-fill", new Vector3(0, 1, -0.2), this.scene).intensity = 0.78;
    const sun = new DirectionalLight("hall-daylight", new Vector3(-0.25, -0.85, -0.35), this.scene);
    sun.position.set(3, 5, -2); sun.intensity = 1.05;
    const tileA = this.material("ivory-tile", "#c9c4b7");
    const tileB = this.material("warm-tile", "#ada89e");
    const grout = this.material("grout", "#626666");
    const wall = this.material("gallery-wall", "#dddcd6");
    const ceiling = this.material("gallery-ceiling", "#555b5e");
    const dark = this.material("display-dark", "#30383c");
    const blue = this.material("sculpture-blue", "#54798c");
    const ochre = this.material("sculpture-ochre", "#b27b3f");
    const rust = this.material("sculpture-rust", "#8f5042");

    this.box("floor-base", 12, 0.12, 9, new Vector3(0, -0.1, 0), grout);
    for (let row = 0; row < 9; row += 1) for (let column = 0; column < 12; column += 1) {
      this.box(`tile-${row}-${column}`, 0.96, 0.025, 0.96, new Vector3(-5.5 + column, -0.02, -4 + row), (row + column) % 3 === 0 ? tileB : tileA);
    }
    this.box("left-wall", 0.15, 3.5, 9, new Vector3(-5.95, 1.75, 0), wall);
    this.box("right-wall", 0.15, 3.5, 9, new Vector3(5.95, 1.75, 0), wall);
    this.box("back-wall", 12, 3.5, 0.15, new Vector3(0, 1.75, 4.45), wall);
    this.box("ceiling", 12, 0.12, 9, new Vector3(0, 3.45, 0), ceiling);
    this.box("feature-panel", 4.6, 2.35, 0.09, new Vector3(0, 1.72, 4.34), dark);
    this.box("left-art", 1.25, 1.55, 0.06, new Vector3(-4.2, 1.82, 4.32), blue);
    this.box("right-art", 1.25, 1.55, 0.06, new Vector3(4.2, 1.82, 4.32), ochre);

    const pedestalPositions = [new Vector3(-2.5, 0.45, 0.9), new Vector3(0, 0.55, 1.8), new Vector3(2.55, 0.4, 0.55)];
    pedestalPositions.forEach((position, index) => {
      this.box(`pedestal-${index}`, 0.9, position.y * 2, 0.9, new Vector3(position.x, position.y, position.z), wall);
    });
    const sculptureA = MeshBuilder.CreateTorusKnot("sculpture-a", { radius: 0.34, tube: 0.12, radialSegments: 32, tubularSegments: 12 }, this.scene);
    sculptureA.position.set(-2.5, 1.38, 0.9); sculptureA.scaling.setAll(0.82); sculptureA.material = blue;
    const sculptureB = MeshBuilder.CreatePolyhedron("sculpture-b", { type: 2, size: 0.62 }, this.scene);
    sculptureB.position.set(0, 1.62, 1.8); sculptureB.rotation.set(0.25, 0.55, 0.12); sculptureB.material = ochre;
    const sculptureC = MeshBuilder.CreateCylinder("sculpture-c", { height: 0.9, diameterTop: 0.18, diameterBottom: 0.62, tessellation: 7 }, this.scene);
    sculptureC.position.set(2.55, 1.25, 0.55); sculptureC.rotation.z = 0.2; sculptureC.material = rust;
    this.box("bench-seat", 2.1, 0.18, 0.55, new Vector3(-3.9, 0.48, -2.25), dark);
    for (const x of [-4.65, -3.15]) this.box(`bench-leg-${x}`, 0.12, 0.48, 0.38, new Vector3(x, 0.24, -2.25), dark);
    for (let index = 0; index < 5; index += 1) {
      this.box(`track-${index}`, 0.08, 0.08, 5.4, new Vector3(-4.4 + index * 2.2, 3.27, 0), dark);
      const light = new PointLight(`spot-${index}`, new Vector3(-4.4 + index * 2.2, 3.1, 0.8), this.scene);
      light.diffuse = Color3.FromHexString("#fff1d2"); light.intensity = 0.16; light.range = 4;
    }
  }

  private buildGravelPark(): void {
    this.scene.clearColor = Color4.FromHexString("#a8cbd8ff");
    this.scene.fogMode = Scene.FOGMODE_LINEAR; this.scene.fogStart = 14; this.scene.fogEnd = 29; this.scene.fogColor = Color3.FromHexString("#a8cbd8");
    new HemisphericLight("park-fill", new Vector3(0.2, 1, 0.1), this.scene).intensity = 0.9;
    const sun = new DirectionalLight("park-sun", new Vector3(-0.4, -0.85, 0.3), this.scene);
    sun.position.set(6, 10, -8); sun.intensity = 1.05;
    const grass = this.material("park-grass", "#658455");
    const gravel = [this.material("gravel-light", "#b0a68d"), this.material("gravel-mid", "#8c8678"), this.material("gravel-dark", "#68665f")] as const;
    const trunk = this.material("park-trunk", "#66503a");
    const foliage = [this.material("leaf-mid", "#4d7650"), this.material("leaf-light", "#668c59"), this.material("leaf-dark", "#365c42")] as const;
    const metal = this.material("park-metal", "#29363a");
    const wood = this.material("park-bench-wood", "#8d5d35");

    this.box("terrain", 15, 0.18, 21, new Vector3(0, -0.16, 0), grass);
    this.box("gravel-bed", 3.5, 0.09, 21, new Vector3(0, -0.025, 0), gravel[1]);
    for (let index = 0; index < 180; index += 1) {
      const rock = MeshBuilder.CreatePolyhedron(`pebble-${index}`, { type: index % 4, size: 0.1 + this.random() * 0.12 }, this.scene);
      rock.position.set(-1.62 + this.random() * 3.24, 0.04, -10 + this.random() * 20);
      rock.scaling.set(1.2, 0.35, 0.8); rock.rotation.y = this.random() * Math.PI; rock.material = gravel[index % gravel.length] ?? gravel[0];
    }
    for (let index = 0; index < 18; index += 1) {
      const side = index % 2 === 0 ? -1 : 1;
      const x = side * (3.1 + this.random() * 3.5);
      const z = -9 + Math.floor(index / 2) * 2.1 + this.random() * 0.8;
      this.tree(`park-tree-${index}`, new Vector3(x, 0, z), trunk, foliage[index % foliage.length] ?? foliage[0], 2.8 + this.random() * 1.2, 1.15);
    }
    for (let index = 0; index < 20; index += 1) {
      const side = index % 2 === 0 ? -1 : 1;
      const shrub = MeshBuilder.CreateSphere(`shrub-${index}`, { diameter: 0.85, segments: 6 }, this.scene);
      shrub.scaling.set(1.15, 0.65, 0.8); shrub.position.set(side * (2.4 + this.random() * 2), 0.38, -9 + this.random() * 18); shrub.material = foliage[(index + 1) % foliage.length] ?? foliage[0];
    }
    this.parkBench(new Vector3(3.15, 0, -0.5), Math.PI / 2, wood, metal);
    this.parkBench(new Vector3(-3.2, 0, 5), -Math.PI / 2, wood, metal);
    for (const z of [-5.5, 1.2, 7]) this.lampPost(new Vector3(-2.45, 0, z), metal);
  }

  private buildLeafForest(): void {
    this.scene.clearColor = Color4.FromHexString("#a9aba0ff");
    this.scene.fogMode = Scene.FOGMODE_LINEAR; this.scene.fogStart = 10; this.scene.fogEnd = 24; this.scene.fogColor = Color3.FromHexString("#a9aba0");
    new HemisphericLight("forest-fill", new Vector3(-0.1, 1, 0.2), this.scene).intensity = 0.76;
    const sun = new DirectionalLight("forest-sun", new Vector3(0.35, -0.88, -0.15), this.scene);
    sun.position.set(-5, 9, -4); sun.intensity = 0.82;
    const soil = this.material("forest-soil", "#5b5142");
    const moss = this.material("forest-moss", "#536345");
    const path = this.material("forest-path", "#75624b");
    const trunk = this.material("forest-trunk", "#594231");
    const leaves = [
      this.material("autumn-orange", "#b56b32"),
      this.material("autumn-ochre", "#b18b3d"),
      this.material("autumn-rust", "#8e4933"),
      this.material("autumn-brown", "#6f4b34"),
    ] as const;
    const stone = this.material("forest-stone", "#666862");

    this.box("forest-ground", 15, 0.2, 21, new Vector3(0, -0.18, 0), moss);
    this.box("forest-path", 3.7, 0.08, 21, new Vector3(0, -0.03, 0), path);
    for (let index = 0; index < 250; index += 1) {
      const leaf = this.box(`fallen-leaf-${index}`, 0.18 + this.random() * 0.18, 0.018, 0.08 + this.random() * 0.1, new Vector3(-1.75 + this.random() * 3.5, 0.03 + this.random() * 0.015, -10 + this.random() * 20), leaves[index % leaves.length] ?? leaves[0]);
      leaf.rotation.y = this.random() * Math.PI;
    }
    for (let index = 0; index < 28; index += 1) {
      const side = index % 2 === 0 ? -1 : 1;
      const x = side * (2.6 + this.random() * 4.1);
      const z = -9.7 + Math.floor(index / 2) * 1.48 + this.random() * 0.7;
      this.tree(`forest-tree-${index}`, new Vector3(x, 0, z), trunk, leaves[index % leaves.length] ?? leaves[0], 3.7 + this.random() * 1.9, 1.35);
    }
    for (let index = 0; index < 12; index += 1) {
      const rock = MeshBuilder.CreatePolyhedron(`forest-rock-${index}`, { type: index % 4, size: 0.3 + this.random() * 0.28 }, this.scene);
      const side = index % 2 === 0 ? -1 : 1;
      rock.position.set(side * (2.1 + this.random() * 2.3), 0.12, -8 + this.random() * 16); rock.scaling.y = 0.65; rock.material = stone;
    }
    const log = MeshBuilder.CreateCylinder("fallen-log", { height: 2.4, diameter: 0.36, tessellation: 9 }, this.scene);
    log.position.set(-2.55, 0.3, 2.2); log.rotation.z = Math.PI / 2; log.rotation.y = 0.35; log.material = trunk;
    this.box("leaf-bank-left", 1.1, 0.09, 6.5, new Vector3(-2.15, 0.01, 0.5), soil).rotation.y = -0.04;
  }

  private tree(name: string, position: Vector3, trunkMaterial: StandardMaterial, crownMaterial: StandardMaterial, height: number, crownSize: number): void {
    const stem = MeshBuilder.CreateCylinder(`${name}-trunk`, { height, diameterTop: 0.26, diameterBottom: 0.42, tessellation: 8 }, this.scene);
    stem.position.set(position.x, height / 2, position.z); stem.material = trunkMaterial;
    const crown = MeshBuilder.CreatePolyhedron(`${name}-crown`, { type: 1, size: crownSize }, this.scene);
    crown.position.set(position.x, height + crownSize * 0.45, position.z); crown.scaling.set(1.15, 1.25, 1.05); crown.rotation.y = this.random() * Math.PI; crown.material = crownMaterial;
  }

  private parkBench(position: Vector3, yaw: number, wood: StandardMaterial, metal: StandardMaterial): void {
    const seat = this.box(`bench-seat-${position.z}`, 1.8, 0.16, 0.5, new Vector3(position.x, 0.55, position.z), wood); seat.rotation.y = yaw;
    const back = this.box(`bench-back-${position.z}`, 1.8, 0.62, 0.12, new Vector3(position.x + Math.sin(yaw) * 0.2, 0.92, position.z + Math.cos(yaw) * 0.2), wood); back.rotation.y = yaw;
    for (const offset of [-0.62, 0.62]) {
      const leg = this.box(`bench-leg-${position.z}-${offset}`, 0.11, 0.55, 0.34, new Vector3(position.x + Math.cos(yaw) * offset, 0.28, position.z - Math.sin(yaw) * offset), metal); leg.rotation.y = yaw;
    }
  }

  private lampPost(position: Vector3, material: StandardMaterial): void {
    const pole = MeshBuilder.CreateCylinder(`lamp-pole-${position.z}`, { height: 2.7, diameter: 0.1, tessellation: 10 }, this.scene);
    pole.position.set(position.x, 1.35, position.z); pole.material = material;
    const top = MeshBuilder.CreateSphere(`lamp-top-${position.z}`, { diameter: 0.42, segments: 8 }, this.scene);
    top.position.set(position.x, 2.75, position.z); top.material = this.material(`lamp-glow-${position.z}`, "#e8d59a");
  }

  private box(name: string, width: number, height: number, depth: number, position: Vector3, material: StandardMaterial): Mesh {
    const mesh = MeshBuilder.CreateBox(name, { width, height, depth }, this.scene);
    mesh.position.copyFrom(position); mesh.material = material; return mesh;
  }

  private material(name: string, hex: string, alpha = 1): StandardMaterial {
    const material = new StandardMaterial(name, this.scene);
    material.diffuseColor = Color3.FromHexString(hex); material.specularColor = Color3.FromHexString("#252525"); material.alpha = alpha;
    return material;
  }

  private random(): number {
    this.randomState = (Math.imul(this.randomState ^ (this.randomState >>> 15), 1 | this.randomState) + 0x6d2b79f5) | 0;
    const value = Math.imul(this.randomState ^ (this.randomState >>> 7), 61 | this.randomState) ^ this.randomState;
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  }

  private downloadCurrentView(): void {
    this.scene.render();
    const link = document.createElement("a");
    link.download = `${this.sceneId}-view-${this.activeView + 1}.png`;
    link.href = this.canvas.toDataURL("image/png");
    link.click();
  }
}
